import { DropboxFileItem } from '@common/types/dropbox';
import { DropboxFilesGridView } from '@components/layout/grids';
import { useMediaAssetsContext } from '@mediaAssets/contexts';
import {
    useLazyGetMediaAssetsDropboxFilesLoadedPathsQuery,
    useMediaAssetsFavoriteMutation,
    useMediaAssetsUnfavoriteMutation,
} from '@mediaAssets/services';
import { useCallback, useMemo } from 'react';
import { defineMessage } from 'react-intl';

export const MediaAssetsGridView = () => {
    const { visibleMediaAssets, setVisibleMediaAssets, setGalleryOpen, setSelectedMediaAsset } =
        useMediaAssetsContext();

    const [triggerLoadedPaths] = useLazyGetMediaAssetsDropboxFilesLoadedPathsQuery();
    const [favoriteFile] = useMediaAssetsFavoriteMutation();
    const [unfavoriteFile] = useMediaAssetsUnfavoriteMutation();

    const loadPage = useCallback(
        async ({
            queryPath,
            pageSize,
            loadedPaths,
        }: {
            queryPath: string;
            pageSize: number;
            loadedPaths: string[];
        }) => {
            const pageData = await triggerLoadedPaths(
                {
                    path: queryPath,
                    page_size: pageSize,
                    loaded_paths: loadedPaths,
                },
                true
            ).unwrap();

            return {
                results: pageData.results ?? [],
                count: pageData.count,
            };
        },
        [triggerLoadedPaths]
    );

    const handleFavorite = useCallback(
        async ({ path, folderPath }: { path: string; folderPath: string }) => {
            await favoriteFile({ path, folderPath }).unwrap();
        },
        [favoriteFile]
    );

    const handleUnfavorite = useCallback(
        async ({ path, folderPath }: { path: string; folderPath: string }) => {
            await unfavoriteFile({ path, folderPath }).unwrap();
        },
        [unfavoriteFile]
    );

    const handleMediaItemClick = useCallback(
        (item: DropboxFileItem) => {
            setSelectedMediaAsset(item);
            setGalleryOpen(true);
        },
        [setGalleryOpen, setSelectedMediaAsset]
    );

    const favoriteStates = useMemo(
        () =>
            Object.fromEntries(
                visibleMediaAssets
                    .filter((asset) => Boolean(asset.path))
                    .map((asset) => [asset.path as string, Boolean(asset.is_favorite)])
            ),
        [visibleMediaAssets]
    );

    const handleFavoriteStateChange = useCallback(
        ({ path, isFavorite }: { path: string; isFavorite: boolean }) => {
            setVisibleMediaAssets((prev) =>
                prev.map((asset) => (asset.path === path ? { ...asset, is_favorite: isFavorite } : asset))
            );
            setSelectedMediaAsset((prev) => (prev && prev.path === path ? { ...prev, is_favorite: isFavorite } : prev));
        },
        [setSelectedMediaAsset, setVisibleMediaAssets]
    );

    return (
        <DropboxFilesGridView
            routeSegment="media-assets"
            downloadType="media_assets"
            loadErrorMessage={defineMessage({
                id: 'mediaAssets.loadError',
                defaultMessage: 'Error loading media assets',
            })}
            loadFailedMessage={defineMessage({
                id: 'media.loadFailed',
                defaultMessage: 'Failed to load media assets.',
            })}
            emptyListMessage={defineMessage({
                id: 'media.noAssets',
                defaultMessage: 'No media assets found in this folder.',
            })}
            loadPage={loadPage}
            onFavorite={handleFavorite}
            onUnfavorite={handleUnfavorite}
            onVisibleMediaItemsChange={setVisibleMediaAssets}
            onMediaItemClick={handleMediaItemClick}
            externalFavoriteStates={favoriteStates}
            onFavoriteStateChange={handleFavoriteStateChange}
        />
    );
};
